// src/components/DepthOcclusionProvider.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { useThree, useFrame } from "@react-three/fiber";

const DepthContext = createContext(null);

export const useDepthOcclusion = () => useContext(DepthContext);
export const useDepthTexture = () => {
  const ctx = useContext(DepthContext);
  return ctx?.depthTexture ?? null;
};

function readStepFromUrlOrStorage(defaultStep = 8) {
  try {
    const url = new URL(window.location.href);
    const fromUrl = Number(url.searchParams.get("depthStep"));
    if (Number.isFinite(fromUrl) && fromUrl >= 1) return fromUrl;

    const fromStorage = Number(localStorage.getItem("depthStep"));
    if (Number.isFinite(fromStorage) && fromStorage >= 1) return fromStorage;
  } catch {}
  return defaultStep;
}

function writeStepToStorage(step) {
  try {
    localStorage.setItem("depthStep", String(step));
  } catch {}
}

export default function DepthOcclusionProvider({ children, initialStep, active = false }) {
  const [step, setStep] = useState(() =>
    Number.isFinite(initialStep) ? initialStep : readStepFromUrlOrStorage(8)
  );
  useEffect(() => {
    if (active && step < 8) setStep(8);
    if (!active && step !== 1) setStep(1); // optional
  }, [active]); // intentionally NOT depending on step

  const mannequinRootRef = useRef(null);
  const mannequinReadyRef = useRef(false);
  const [mannequinVersion, setMannequinVersion] = useState(0);

  // Runtime-owned refs
  const rtRef = useRef(null);
  const depthSceneRef = useRef(null);

  const api = useMemo(
    () => ({
      step,
      setStep: (next) => {
        const n = Number(next);
        const safe = Number.isFinite(n) && n >= 1 ? n : 1;
        writeStepToStorage(safe);
        setStep(safe);
      },

      enabled: step >= 2,

      // CRITICAL: this is always the real RT texture (or null)
      get depthTexture() {
        return rtRef.current?.depthTexture ?? null;
      },

      mannequinVersion,

      registerMannequinRoot: (root) => {
        const next = root ?? null;
        if (mannequinRootRef.current === next) return;
        mannequinRootRef.current = next;
        setMannequinVersion((v) => v + 1);
      },

      setMannequinReady: (ready) => {
        mannequinReadyRef.current = !!ready;
      },

      _mannequinRootRef: mannequinRootRef,
      _mannequinReadyRef: mannequinReadyRef,
      _rtRef: rtRef,
      _depthSceneRef: depthSceneRef,
    }),
    [step, mannequinVersion]
  );

  return (
    <DepthContext.Provider value={api}>
      {step >= 2 ? <DepthRuntime /> : null}
      {children}
    </DepthContext.Provider>
  );
}

function DepthRuntime() {
  const ctx = useDepthOcclusion();
  const { gl, size, camera } = useThree();

  useEffect(() => {
  // dispose old
  try {
    if (ctx._rtRef.current) ctx._rtRef.current.dispose();
  } catch {}
  ctx._rtRef.current = null;

  if (ctx.step < 2) return;

  try {
    const rt = new THREE.WebGLRenderTarget(256, 256, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: true,
      stencilBuffer: false,
    });

    // Attach a real DepthTexture (this is what we'll sample)
    const dt = new THREE.DepthTexture(256, 256);
    dt.format = THREE.DepthFormat;
    dt.type = THREE.UnsignedShortType; // stable default
    rt.depthTexture = dt;

    ctx._rtRef.current = rt;

    return () => {
      try {
        rt.dispose();
      } catch {}
    };
  } catch (e) {
    console.error("[DepthRuntime] RT allocation error:", e);
  }
}, [ctx.step]);


  // Resize RT to drawing buffer
  useEffect(() => {
  const rt = ctx._rtRef.current;
  if (!rt) return;
  if (ctx.step < 2) return;

  const dpr = gl.getPixelRatio();
  const w = Math.max(1, Math.floor(size.width * dpr));
  const h = Math.max(1, Math.floor(size.height * dpr));
  rt.setSize(w, h);

  // keep DepthTexture dimensions in sync too
  if (rt.depthTexture) {
    rt.depthTexture.image.width = w;
    rt.depthTexture.image.height = h;
    rt.depthTexture.needsUpdate = true;
  }
}, [ctx.step, gl, size.width, size.height]);


  // Build depth scene clone
  useEffect(() => {
    if (ctx.step < 5) {
      ctx._depthSceneRef.current = null;
      return;
    }

    const root = ctx._mannequinRootRef.current;
    const ready = ctx._mannequinReadyRef.current;
    if (!root || !ready) return;

    const depthScene = new THREE.Scene();
    ctx._depthSceneRef.current = depthScene;

    const clone = root.clone(true);

    // TEMP: obvious color so we can prove RT color is updating
    const mat = new THREE.MeshBasicMaterial({ color: 0xff00ff });

    clone.traverse((obj) => {
      if (!obj.isMesh) return;

      const n = (obj.name || "").toLowerCase();
      if (n.includes("garment") || n.includes("cloth") || n.includes("overlay")) {
        obj.visible = false;
        return;
      }

      obj.material = mat;
      obj.frustumCulled = false;
    });

    clone.frustumCulled = false;
    depthScene.add(clone);
  }, [ctx.step, ctx.mannequinVersion]);

  // Render into RT (main camera)
useFrame(() => {
  if (ctx.step < 8) return;

  const rt = ctx._rtRef.current;
  const depthScene = ctx._depthSceneRef.current;
  if (!rt || !depthScene) return;

  const prevTarget = gl.getRenderTarget();
  const prevAutoClear = gl.autoClear;

  // ✅ Save current viewport/scissor state EXACTLY
  const prevViewport = new THREE.Vector4();
  const prevScissor = new THREE.Vector4();
  gl.getViewport(prevViewport);
  gl.getScissor(prevScissor);
  const prevScissorTest = gl.getScissorTest();

  try {
    gl.autoClear = false;
    gl.setRenderTarget(rt);

    const dpr = gl.getPixelRatio();
    const w = Math.floor(size.width * dpr);
    const h = Math.floor(size.height * dpr);

    gl.setViewport(0, 0, w, h);
    gl.setScissor(0, 0, w, h);
    gl.setScissorTest(true);

    gl.clear(true, true, true);
    camera.updateMatrixWorld();
    gl.render(depthScene, camera);
  } catch (e) {
    console.error("[DepthRuntime] depth pass error:", e);
  } finally {
    // ✅ Restore EXACT previous state
    gl.setScissorTest(prevScissorTest);
    gl.setScissor(prevScissor);
    gl.setViewport(prevViewport);

    gl.setRenderTarget(prevTarget);
    gl.autoClear = prevAutoClear;
  }
});

  return null;
}
